import { useEffect, useRef } from "react";

// Her exit from the Portal: she breaks into stardust and drifts up into the
// sky while her (real) photo layer fades underneath.
//
// Particles only spawn inside her silhouette. The mask (`mask`) was made by
// diffing the Portal art against the transition's clean first frame, so it
// is exactly where she stands. Each particle takes its colour from the
// Portal poster at that spot, so it's literally her image coming apart:
// fur-white, satin-red, skin, hair. It warms slightly toward magenta as it
// rises.
//
// The canvas mirrors the video's object-fit: cover + object-position, so the
// dust sits on her at any viewport size.
const DUST_TINT = [255, 120, 200];
// How far each mote drifts toward DUST_TINT as it rises (0 = stays her own
// colour). Turned down 75% from 1.0 per Trevor.
const TINT_AMOUNT = 0.25;

export const StardustDissolve = ({
  active,
  mask,
  poster,
  objectPosition = [0.5, 0.5],
  count = 350,
  // Scales the head-to-feet spawn delay, so the dust can burst as fast as
  // her photo fades (desktop uses 0.25).
  stagger = 1,
  className = "",
}) => {
  const canvasRef = useRef(null);
  const seedsRef = useRef(null); // [{u, v, r, g, b}] in 0..1 video space
  const aspectRef = useRef(16 / 9);
  const rafRef = useRef(0);

  // Build the particle seeds once: sample the mask, colour from the poster.
  useEffect(() => {
    let cancelled = false;
    const load = (src) =>
      new Promise((res, rej) => {
        const img = new Image();
        img.onload = () => res(img);
        img.onerror = rej;
        img.src = src;
      });
    Promise.all([load(mask), load(poster)])
      .then(([m, p]) => {
        if (cancelled) return;
        aspectRef.current = p.naturalWidth / p.naturalHeight;
        const w = m.naturalWidth;
        const h = m.naturalHeight;
        const c = document.createElement("canvas");
        c.width = w;
        c.height = h;
        const ctx = c.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(m, 0, 0, w, h);
        const md = ctx.getImageData(0, 0, w, h).data;
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(p, 0, 0, w, h);
        const pd = ctx.getImageData(0, 0, w, h).data;
        const inside = [];
        for (let i = 0; i < w * h; i++) if (md[i * 4] > 128) inside.push(i);
        if (!inside.length) return;
        const seeds = [];
        for (let k = 0; k < count; k++) {
          const i = inside[(Math.random() * inside.length) | 0];
          const x = (i % w) + Math.random();
          const y = ((i / w) | 0) + Math.random();
          seeds.push({ u: x / w, v: y / h, r: pd[i * 4], g: pd[i * 4 + 1], b: pd[i * 4 + 2] });
        }
        seedsRef.current = seeds;
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [mask, poster, count]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    cancelAnimationFrame(rafRef.current);

    const size = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = canvas.clientWidth * dpr;
      canvas.height = canvas.clientHeight * dpr;
      return dpr;
    };

    if (!active || !seedsRef.current) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    const dpr = size();
    const W = canvas.width;
    const H = canvas.height;
    // object-fit: cover geometry of the video under us.
    const vAspect = aspectRef.current;
    const scale = Math.max(W / vAspect, H); // rendered video height in px
    const vw = scale * vAspect;
    const vh = scale;
    const ox = (W - vw) * objectPosition[0];
    const oy = (H - vh) * objectPosition[1];

    const parts = seedsRef.current.map((s) => {
      // Head first: the top of her lifts off before her feet.
      const delay = (s.v * 0.55 + Math.random() * 0.25) * stagger;
      return {
        x: ox + s.u * vw,
        y: oy + s.v * vh,
        vx: (Math.random() - 0.5) * 40 * dpr,
        vy: -(25 + Math.random() * 70) * dpr,
        ay: -(40 + Math.random() * 60) * dpr,
        wob: Math.random() * Math.PI * 2,
        size: (0.6 + Math.random() * 1.6) * dpr,
        delay,
        life: 1.1 + Math.random() * 1.1,
        r: Math.min(255, s.r * 1.25 + 30),
        g: Math.min(255, s.g * 1.25 + 30),
        b: Math.min(255, s.b * 1.25 + 30),
      };
    });

    const start = performance.now();
    const end = Math.max(...parts.map((q) => q.delay + q.life));
    const tick = (now) => {
      const t = (now - start) / 1000;
      ctx.clearRect(0, 0, W, H);
      ctx.globalCompositeOperation = "lighter";
      for (const q of parts) {
        const a = t - q.delay;
        if (a < 0 || a > q.life) continue;
        const k = a / q.life; // 0..1 through its life
        const x = q.x + q.vx * a + Math.sin(q.wob + a * 3) * 6 * dpr * k;
        const y = q.y + q.vy * a + 0.5 * q.ay * a * a;
        const alpha = Math.min(1, a / 0.12) * (1 - k) * (1 - k);
        const tint = Math.min(1, k * 1.4) * TINT_AMOUNT;
        const r = q.r + (DUST_TINT[0] - q.r) * tint;
        const g = q.g + (DUST_TINT[1] - q.g) * tint;
        const b = q.b + (DUST_TINT[2] - q.b) * tint;
        const sz = q.size * (1 + k * 0.6);
        ctx.fillStyle = `rgba(${r | 0},${g | 0},${b | 0},${alpha * 0.9})`;
        ctx.fillRect(x - sz / 2, y - sz / 2, sz, sz);
        // soft halo on the bigger motes
        if (q.size > 1.6 * dpr) {
          ctx.fillStyle = `rgba(${r | 0},${g | 0},${b | 0},${alpha * 0.18})`;
          ctx.fillRect(x - sz * 1.5, y - sz * 1.5, sz * 3, sz * 3);
        }
      }
      if (t < end) rafRef.current = requestAnimationFrame(tick);
      else ctx.clearRect(0, 0, W, H);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [active, objectPosition, stagger]);

  return <canvas ref={canvasRef} aria-hidden className={`pointer-events-none absolute inset-0 h-full w-full ${className}`} />;
};
